import React, { FC, useMemo } from 'react';
import cx from 'classnames';
import styles from '../Step3.module.scss';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import { RadioGroup } from '@material-ui/core';
import toast from 'react-hot-toast';
import { renderToast } from 'components/CustomToast/CustomToast';
import { batch } from 'react-redux';
import NavigationBtnControl from '../../NavigationBtnControl/NavigationBtnControl';
import {
  BookingInformation,
  popStep,
  pushStep,
  resetBookingForm,
  STEPS,
  updateBookingInformation,
  updateStep3,
} from 'redux/bookingFormSlice';
import { formatDobForBE } from '../../utils/helperFunction';
import { resetSysType } from 'redux/authSlice';
import PatientCard from '../PatientCard/PatientCard';
import NewPatientCard from '../NewPatientCard/NewPatientCard';
import { Patient } from 'services/APIs';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import DetailDescriptionSection from '../../DetailDescriptionSection/DetaiDescriptionSection';
import { setShowInsuranceModal } from 'redux/clinicDetailsSlice';
import { updateSelectedLastInsurance } from 'redux/bookingFormSlice';
import { checkExistingPractitionerAcceptInsurance } from 'utils/checkExistingPractitionerAcceptInsurance';

interface ReviewProps {
  onClose: () => void;
  bookingSummaryInfo: {
    avatar: string;
    title: string;
    serviceTitle: string;
    clinicName: string;
    clinicAddress: string;
    clinicEmail: string;
    clinicPhoneNumber: string;
  };
  isPreferredDoctor?: boolean;
  practitionerId: string;
}

interface BookablePatientInputProps {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  dob: string;
  phoneNumber: string;
}

export interface NewPatient {
  phoneNumber: string;
  dob: string;
  email: string;
  firstName: string;
  lastName: string;
}

export const PATIENT_LIST_TYPE = {
  NORMAL: 'normal-patient-list', // patient is allowed to book next appts
  INCOMPLETE_FUTURE_APPT: 'incomplete-future-appt', // patient who has future appts and is not allowed to book next appts
  INCOMPLETE_PAST_APPT: 'incomplete-past-appt', // patient who has appts in the past and is not allowed to book next appts
  EXISTING_FUTURE_APPT: 'existing-future-appt', // patient who has appt
};

export const NULL_PATIENT_ID = 'patient-id-null';

const Review: FC<ReviewProps> = ({
  onClose,
  bookingSummaryInfo,
  isPreferredDoctor,
}) => {
  const {
    bookingFormSlice: {
      step1,
      step2: { newPatient, signUp },
      step3: {
        patients,
        chosenPatientId,
        insurancesByAvailableDoctors,
        isPayForMyself,
      },
      pageStack,
      selectedLastInsurance,
      bookingInformation,
    },
    authSlice: { isLoggedIn },
    clinicDetailsSlice: { clinicData },
  } = useAppSelector((state) => state);

  const isLoadedInsideIframe = useIsLoadedInsideIframe();

  const containerClassName = cx({
    [styles['container']]: true,
    [styles['container-iframe']]: isLoadedInsideIframe,
  });

  const dispatch = useAppDispatch();

  const previousPage = pageStack[pageStack.length - 2];

  const patient = useMemo(() => {
    return previousPage === STEPS.NEW_PATIENT
      ? { ...step1, ...newPatient, id: '' }
      : { ...signUp, id: '' };
  }, [newPatient, previousPage, signUp, step1]);

  const handleOnBackClick = () => {
    if (isLoggedIn) {
      batch(() => {
        dispatch(resetBookingForm());
        dispatch(resetSysType());
      });
      return onClose();
    }
    dispatch(popStep());
  };

  const handleNotYouClick = () => {
    dispatch(pushStep(STEPS.NEW_PATIENT));
  };

  const normalizedPatientInfo = () => {
    let chosenPatient = null;

    if (chosenPatientId === NULL_PATIENT_ID) {
      chosenPatient = patients.find((patient: any) => patient.id === null);
    } else {
      chosenPatient = patients.find(
        (patient: any) => patient.id === chosenPatientId
      );
    }

    // Detect if a new input patient is the same as the list of patients
    const duplicateNewPatient =
      previousPage === STEPS.NEW_PATIENT &&
      patients.find(
        (patientItem) =>
          patientItem.email?.toLowerCase() === patient.email?.toLowerCase() &&
          patientItem.firstName?.toLowerCase() ===
            patient.firstName?.toLowerCase() &&
          patientItem.lastName?.toLowerCase() ===
            patient.lastName?.toLowerCase()
      );

    const patientInfo =
      chosenPatient && previousPage !== STEPS.NEW_PATIENT
        ? {
            id: chosenPatient.id,
            email: chosenPatient.email,
            firstName: chosenPatient.firstName,
            lastName: chosenPatient.lastName,
            dob: chosenPatient.dob,
            phoneNumber: chosenPatient.phoneNumber,
            isNewPatient: chosenPatient.isNewPatient,
            homeAddress: chosenPatient.homeAddress || '',
            insuranceCoverages: chosenPatient.insuranceCoverages || [],
          }
        : {
            email: patient.email,
            firstName: patient.firstName,
            lastName: patient.lastName,
            dob: formatDobForBE(patient.dob),
            phoneNumber: patient.phoneNumber,
            isNewPatient: true,
            ...(duplicateNewPatient && { id: duplicateNewPatient.id }),
            homeAddress: '',
            insuranceCoverages: [],
          };

    return patientInfo;
  };

  const handleOnSubmit = async () => {
    const patientInfo = normalizedPatientInfo();

    batch(() => {
      dispatch(
        updateStep3({
          patientInfo: { ...patientInfo, note: '' },
        })
      );
      dispatch(pushStep(STEPS.FINAL));
    });
  };

  const checkInsuranceKickback = async () => {
    if (selectedLastInsurance?.insuranceCarrierId) {
      await handleOnSubmit();
      return;
    }

    // Insurance-practitioner validation
    const chosenPatient = patients.find((p) => p.id === chosenPatientId);
    const lastCoverage = chosenPatient?.insuranceCoverages?.[0];
    const insuranceCarrierId = lastCoverage?.insuranceCarrierId;

    const showInsuranceToast = async (message: string) => {
      dispatch(
        updateSelectedLastInsurance(
          insuranceCarrierId && lastCoverage
            ? {
                insuranceCarrierName: lastCoverage.insuranceCarrierName ?? '',
                insuranceCarrierId,
                clinicInsuranceId: lastCoverage.clinicInsuranceId,
              }
            : null
        )
      );
      onClose();

      renderToast({
        type: 'error',
        duration: 8000,
        message: (
          <span>
            {message}{' '}
            <span
              onClick={() => {
                toast.dismiss();
                dispatch(setShowInsuranceModal(true));
              }}
              style={{
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 600,
              }}
            >
              Choose another insurance
            </span>
          </span>
        ),
      });
    };

    if (
      clinicData.isInsuranceRequired &&
      insuranceCarrierId &&
      insurancesByAvailableDoctors.length > 0
    ) {
      if (!bookingInformation || bookingInformation.type !== 'clinic') return;

      const { isAcceptedInsurance, dentistId } =
        checkExistingPractitionerAcceptInsurance({
          doctors: bookingInformation.insurancesByAvailableDoctors || [],
          practitionerId: bookingInformation.practitioner.id,
          currentInsuranceCarrierId: insuranceCarrierId,
          isPreferredDoctor: !!isPreferredDoctor,
          isPayForMyself,
          isExamCleaningService: bookingInformation.isExamCleaningService,
          dentistId: bookingInformation.dentist?.id || undefined,
        });

      if (!isAcceptedInsurance) {
        if (isPreferredDoctor) {
          await showInsuranceToast(
            `This practitioner does not accept ${lastCoverage?.insuranceCarrierName ?? ''}`
          );
        } else {
          await showInsuranceToast(
            `This practitioner does not accept ${lastCoverage?.insuranceCarrierName ?? ''}`
          );
        }

        return;
      }

      if (dentistId) {
        dispatch(
          updateBookingInformation({
            ...bookingInformation,
            dentist: { ...bookingInformation.dentist, id: dentistId },
          } as BookingInformation)
        );
      }
    }

    await handleOnSubmit();
  };

  const renderNotYouSection = () => {
    if (previousPage !== STEPS.PATIENT_INFO) {
      return null;
    }

    return (
      <div className={styles['not-you-section']}>
        not you?
        <button className={styles['click-here']} onClick={handleNotYouClick}>
          click here
        </button>
      </div>
    );
  };

  const renderDetailSection = (type: string) => {
    if (
      type === PATIENT_LIST_TYPE.NORMAL &&
      previousPage !== STEPS.PATIENT_INFO
    ) {
      return null;
    }

    return (
      <DetailDescriptionSection
        type={type}
        patients={patients}
        clinicInfo={{
          name: bookingSummaryInfo.clinicName,
          phoneNumber: bookingSummaryInfo.clinicPhoneNumber,
          email: bookingSummaryInfo.clinicEmail,
        }}
        futureAppointmentWindowDays={clinicData.futureAppointmentWindowDays}
      />
    );
  };

  // Patients who are allowed to book next appts
  const renderBookablePatients = (newPatient?: BookablePatientInputProps) => {
    const bookablePatients = patients.filter((item) => item.isBookable);

    // If a new patient is provided (Path 1), check for duplicates
    let allBookablePatients: Array<Patient | BookablePatientInputProps> = [
      ...bookablePatients,
    ];

    if (newPatient) {
      const duplicatePatient = patients.find(
        (item) =>
          item.email?.toLowerCase() === newPatient.email?.toLowerCase() &&
          item.firstName?.toLowerCase() ===
            newPatient.firstName?.toLowerCase() &&
          item.lastName?.toLowerCase() === newPatient.lastName?.toLowerCase()
      );

      // Only add the new patient if they don't duplicate an existing patient
      if (!duplicatePatient) {
        allBookablePatients = [...allBookablePatients, newPatient];
      }
    }

    if (allBookablePatients.length === 0) return null;

    return (
      <div className={styles['patient-list-section']}>
        <div className={styles['description-section']}>
          {renderDetailSection(PATIENT_LIST_TYPE.NORMAL)}
          {renderNotYouSection()}
        </div>

        <RadioGroup
          className={styles['card-container']}
          value={chosenPatientId || NULL_PATIENT_ID}
          onChange={(e) =>
            dispatch(updateStep3({ chosenPatientId: e.target.value }))
          }
        >
          {allBookablePatients.map((patient) => {
            let normalizedPatient = patient;
            let selectedInsurance = null;

            if (patient.id === chosenPatientId && selectedLastInsurance) {
              selectedInsurance = {
                insuranceCarrierName:
                  selectedLastInsurance.insuranceCarrierName,
                insuranceCarrierId: selectedLastInsurance.insuranceCarrierId,
              };
            }

            return (
              <PatientCard
                key={normalizedPatient.id || NULL_PATIENT_ID}
                isMultipleResult={allBookablePatients.length > 1}
                patient={normalizedPatient}
                chosenPatientId={chosenPatientId}
                selectedInsurance={selectedInsurance}
              />
            );
          })}
        </RadioGroup>
      </div>
    );
  };

  // Patients who are not allowed to book next appts because they have future appts
  const renderUnbookablePatientsHasFutureApptInFuture = () => {
    const bookablePatients = patients.filter((item) => item.isBookable);

    // Exclude patients shown in higher-priority sections
    const newPatients = patients.filter(
      (item) =>
        item.isBookable === false &&
        item.appointment?.isInPast === false &&
        item.isExistingApptWithinDays !== true
    );

    if (newPatients.length === 0) return null;

    return (
      <div className={styles['patient-list-section']}>
        <div className={styles['description-section']}>
          {renderDetailSection(PATIENT_LIST_TYPE.INCOMPLETE_FUTURE_APPT)}
          {bookablePatients.length === 0 && renderNotYouSection()}
        </div>

        <div className={styles['card-container']}>
          {newPatients.map((patient) => (
            <NewPatientCard key={patient.id} patient={patient} />
          ))}
        </div>
      </div>
    );
  };

  // Patients who are not allowed to book next appts because they have the past incomplete appts
  const renderUnbookablePatientsHasIncompleteApptInPast = () => {
    const bookablePatients = patients.filter((item) => item.isBookable);

    // Exclude patients shown in higher-priority sections
    const newPatients = patients.filter(
      (item) =>
        item.isBookable === false &&
        item.appointment?.isInPast === true &&
        item.isExistingApptWithinDays !== true
    );

    if (newPatients.length === 0) return null;

    return (
      <div className={styles['patient-list-section']}>
        <div className={styles['description-section']}>
          {renderDetailSection(PATIENT_LIST_TYPE.INCOMPLETE_PAST_APPT)}
          {bookablePatients.length === 0 && renderNotYouSection()}
        </div>

        <div className={styles['card-container']}>
          {newPatients.map((patient) => (
            <NewPatientCard key={patient.id} patient={patient} />
          ))}
        </div>
      </div>
    );
  };

  // Patients who are not allowed to book next appts because they have the future conflicted appts within X days
  const renderUnbookablePatientWithFutureConflictedApptWithinDays = () => {
    const bookablePatients = patients.filter((item) => item.isBookable);

    const patientsWithApptWithinDays = patients.filter(
      (item) =>
        item.isBookable === false && item.isExistingApptWithinDays === true
    );

    if (patientsWithApptWithinDays.length === 0) return null;

    return (
      <div className={styles['patient-list-section']}>
        <div className={styles['description-section']}>
          {renderDetailSection(PATIENT_LIST_TYPE.EXISTING_FUTURE_APPT)}
          {bookablePatients.length === 0 && renderNotYouSection()}
        </div>

        <div className={styles['card-container']}>
          {patientsWithApptWithinDays.map((patient) => (
            <NewPatientCard key={patient.id} patient={patient} />
          ))}
        </div>
      </div>
    );
  };

  const handleOnDone = () => {
    window.location.reload();
  };

  const isDoneButton = useMemo(() => {
    if (!patient.id && patient.email) {
      // Patient is from New Patient form
      const existingPatientInList = patients.find(
        (item) =>
          item.email.toLowerCase() === patient.email.toLowerCase() &&
          item.firstName.toLowerCase() === patient.firstName.toLowerCase() &&
          item.lastName.toLowerCase() === patient.lastName.toLowerCase()
      );

      if (existingPatientInList) {
        return existingPatientInList.isBookable === false;
      }

      return false;
    }

    const bookablePatients = patients.filter(
      (item) => item.isBookable === true
    );

    return bookablePatients.length === 0;
  }, [patient, patients]);

  return (
    <div className={containerClassName}>
      {patient.email ? (
        <>
          {renderBookablePatients({
            id: patient.id,
            email: patient.email,
            firstName: patient.firstName,
            lastName: patient.lastName,
            dob: patient.dob,
            phoneNumber: patient.phoneNumber,
          })}
          {renderUnbookablePatientsHasFutureApptInFuture()}
          {renderUnbookablePatientsHasIncompleteApptInPast()}
          {renderUnbookablePatientWithFutureConflictedApptWithinDays()}
        </>
      ) : (
        <>
          {renderBookablePatients()}
          {renderUnbookablePatientsHasFutureApptInFuture()}
          {renderUnbookablePatientsHasIncompleteApptInPast()}
          {renderUnbookablePatientWithFutureConflictedApptWithinDays()}
        </>
      )}
      <NavigationBtnControl
        nextBtnTitle={isDoneButton ? 'Done' : 'Next'}
        onBack={handleOnBackClick}
        onNext={isDoneButton ? handleOnDone : checkInsuranceKickback}
      />
    </div>
  );
};

export default Review;
